import { useEffect, useState } from "react";
import { Typography, Form, Image, Alert, Checkbox } from "antd";
import CustomButton from "../../components/CustomButton/Button";
import CustomInput from "../../components/CustomInput/Input";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import Logo from "../../assets/images/auth-logo.png";
import CryptoJS from "crypto-js";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
  confirmPasswordRules,
  passwordRules,
  phoneNumberRules,
  companyNameRules,
  nameRules,
  emailRules,
} from "../../helpers/helpers";
import {
  signupUser,
  getAllDesignations,
  clearAuthError,
  registerSendOtp,
  getRmCities,
} from "../../redux/slices/AuthSlice";
import {
  BTN_TXT,
  ERROR_MSGS,
  GLOBAL_CONSTANTS,
  TITLE_TXT,
  USERTYPE_ID,
  DESC_TXT,
} from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import CustomModal from "../../components/CustomModal/CustomModal";
import CountryCode from "../../components/CountryCodeSelect/CountryCode";
import TermsPdf from "../../assets/docs/TermsAndConditions.pdf";
import PrivacyPdf from "../../assets/docs/PrivacyPolicies.pdf";
import OtpVerification from "./OtpVerification";

const { Title, Text } = Typography;

const CompanySignup = ({ signupType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const pocPhoneNumber = Form.useWatch("pocPhoneNumber", form);
  const pocEmail = Form.useWatch("pocEmail", form);
  const { designations, error, loading } = useSelector((state) => state.auth);
  const [visible, setVisible] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [isVerified, setIsVerified] = useState({
    phoneNumber: false,
    email: false,
  });
  const [otpContext, setOtpContext] = useState({ type: "", value: "" });

  const isNbfc = signupType === USERTYPE_ID.NBFC;

  useEffect(() => {
    dispatch(getAllDesignations());
    dispatch(getRmCities());
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  const toggleVisibility = () => {
    setVisible(!visible);
  };

  const handleVerifyBtnClick = async (type) => {
    if (type === "pocPhoneNumber") {
      await dispatch(registerSendOtp({ mobile: pocPhoneNumber }));
      setOtpContext({
        type: "phoneNumber",
        value: pocPhoneNumber,
      });
      setOtpModalOpen(true);
    } else {
      await dispatch(registerSendOtp({ email: pocEmail }));
      setOtpContext({ type: "email", value: pocEmail });
      setOtpModalOpen(true);
    }
  };

  const handleSubmit = async (values) => {
    const hashedPassword = CryptoJS.SHA256(values.enterPassword).toString();

    const payload = {
      email: values.pocEmail,
      password: hashedPassword,
      name: values.companyName,
      contactName: values.pocName,
      phoneNumber: values.pocPhoneNumber,
      designationId: values.pocDesignation,
      investorType: signupType,
      isEmailVerified: isVerified.email,
      isPhoneNumberVerified: isVerified.phoneNumber,
      referralCode: values.referralCode,
    };

    const res = await dispatch(signupUser(payload));

    if (res.payload.statusCode === 201) {
      form.resetFields();
      navigate("/login");
    }
  };

  return (
    <div className="auth-container">
      <div className="logo">
        <Image preview={false} src={Logo} alt="Logo" />
      </div>

      <Title className="hello-text">{TITLE_TXT.LETS_GET_START_TEXT}</Title>

      <Alert
        message={DESC_TXT.POC_DIRECTOR_NOTE}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        className="signup-form"
        onFinish={handleSubmit}
        initialValues={{ remember: true, countryCode: "+91" }}
      >
        <CustomInput
          label={`${isNbfc ? "NBFC" : "Entity"} Name`}
          name="companyName"
          type="text"
          placeholder={`Enter ${isNbfc ? "NBFC" : "Entity"} Name`}
          rules={companyNameRules(isNbfc ? "NBFC" : "Entity")}
        />

        <CustomInput
          label="Point of Contact Name"
          name="pocName"
          type="text"
          placeholder="Enter Name"
          rules={nameRules("Point of Contact")}
        />

        <CustomInput
          label="Point of Contact Phone Number (Should be linked with Aadhaar)"
          name="pocPhoneNumber"
          addonBefore={<CountryCode />}
          placeholder="Phone Number"
          rules={phoneNumberRules("Point of Contact")}
          verifyBtn={true}
          disabled={isVerified.phoneNumber}
          isVerified={isVerified.phoneNumber}
          disableVerify={
            isVerified.phoneNumber || !/^[6-9]\d{9}$/.test(pocPhoneNumber || "")
          }
          handleVerify={() => handleVerifyBtnClick("pocPhoneNumber")}
        />

        <CustomInput
          label="Point of Contact Email"
          name="pocEmail"
          type="email"
          placeholder="Enter Email"
          rules={emailRules("Point of Contact")}
          verifyBtn={true}
          disabled={isVerified.email}
          isVerified={isVerified.email}
          disableVerify={
            isVerified.email ||
            !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(pocEmail || "")
          }
          handleVerify={() => handleVerifyBtnClick("pocEmail")}
        />

        {otpModalOpen && (
          <CustomModal
            open={otpModalOpen}
            centered
            onCancel={() => setOtpModalOpen(false)}
            content={
              <OtpVerification
                contactType={otpContext.type}
                contactValue={otpContext.value}
                setIsVerified={setIsVerified}
                setOtpModalOpen={setOtpModalOpen}
              />
            }
          />
        )}

        <CustomSelect
          label="Point of Contact Designation (choose 'Other' if not listed)"
          name="pocDesignation"
          showSearch
          placeholder="Select Designation"
          className="custom-designation-select"
          options={designations.map((designation) => ({
            value: designation.designationId,
            label: designation.designationName.replace(/_/g, " "),
          }))}
          rules={[{ required: true, message: ERROR_MSGS.DESIGNATION_ERR_MEG }]}
        />

        <CustomInput
          label="Enter New Password"
          name="enterPassword"
          type={visible ? "text" : "password"}
          isPassword={true}
          iconRender={(visible) =>
            visible ? (
              <EyeInvisibleOutlined onClick={toggleVisibility} />
            ) : (
              <EyeOutlined onClick={toggleVisibility} />
            )
          }
          placeholder="Enter Password"
          rules={passwordRules}
        />

        <CustomInput
          label="Confirm New Password"
          name="confirmPassword"
          type={visible ? "text" : "password"}
          isPassword={true}
          iconRender={(visible) =>
            visible ? (
              <EyeInvisibleOutlined onClick={toggleVisibility} />
            ) : (
              <EyeOutlined onClick={toggleVisibility} />
            )
          }
          placeholder="Re-enter Password"
          rules={confirmPasswordRules}
        />

        <CustomInput
          label="Referral Code"
          name="referralCode"
          type="text"
          placeholder="Enter referral code"
        />

        <Form.Item
          rules={[{ required: true, message: ERROR_MSGS.ACCP_TER_CON }]}
        >
          <Checkbox
            checked={isTermsAccepted}
            onChange={(e) => setIsTermsAccepted(e.target.checked)}
          >
            By checking this box, I confirm that I have read, understood and
            agreed to be bound by the{" "}
            <span
              style={{
                color: "#2243B6",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setIsTermsModalOpen(true)}
            >
              Terms & Conditions
            </span>{" "}
            and{" "}
            <span
              style={{
                color: "#2243B6",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setIsPrivacyModalOpen(true)}
            >
              Privacy Policy
            </span>
          </Checkbox>
        </Form.Item>

        <CustomModal
          open={isTermsModalOpen}
          centered
          width="70%"
          onCancel={() => setIsTermsModalOpen(false)}
          content={
            <iframe
              title="Terms and Privacy"
              src={TermsPdf}
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          }
        />

        <CustomModal
          open={isPrivacyModalOpen}
          centered
          width="70%"
          onCancel={() => setIsPrivacyModalOpen(false)}
          content={
            <iframe
              title="Terms and Privacy"
              src={PrivacyPdf}
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          }
        />

        {error && (
          <Alert
            style={{ marginBottom: 20 }}
            message={error}
            type="error"
            showIcon
          />
        )}

        <Form.Item>
          <CustomButton
            loading={loading}
            disabled={!isTermsAccepted}
            type="primary"
            htmlType="submit"
            block
          >
            {BTN_TXT.SIGNUP}
          </CustomButton>
        </Form.Item>
      </Form>
      <Text className="already-user">
        {GLOBAL_CONSTANTS.ALREADY_HAVE_ACC}{" "}
        <Link to="/login">
          <span>{BTN_TXT.LOGIN}</span>
        </Link>
      </Text>
    </div>
  );
};
export default CompanySignup;
